import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowDropDown, Close } from "@mui/icons-material";
import SearchBox from "./searchbox";

type OptionType = {
  value: number;
  label: string;
};

type Props = {
  label?: string;
  options: OptionType[];
  value: number[];
  onChange: (values: number[]) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  singlePreview?: boolean,
  showSelectAll?: boolean;
};

const MultiSelectDropdown: React.FC<Props> = ({
  label,
  options,
  value = [],
  onChange,
  placeholder = "-Select-",
  className = "",
  required = false,
  disabled = false,
  singlePreview = false,
  showSelectAll = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const handleOpen = () => {
    if (disabled) return;

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      const dropdownHeight = 340;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenTop = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

      setDropdownPosition({
        top: shouldOpenTop
          ? rect.top + window.scrollY - dropdownHeight
          : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setIsOpen((prev) => !prev);
  };

  const toggleOption = (optionValue: number) => {
    let newValues = [...value];

    if (newValues.includes(optionValue)) {
      newValues = newValues.filter((v) => v !== optionValue);
    } else {
      newValues.push(optionValue);
    }

    onChange(newValues);
  };

  const removeTag = (val: number) => {
    onChange(value.filter((v) => v !== val));
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && dropdownRef.current && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenTop =
        spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

      setDropdownPosition({
        top: shouldOpenTop
          ? rect.top + window.scrollY - dropdownHeight
          : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, filteredOptions.length]);

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => value.includes(option.value));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = filteredOptions.map((o) => o.value);

      onChange(value.filter((v) => !filteredIds.includes(v)));
    } else {
      const filteredIds = filteredOptions.map((o) => o.value);

      const mergedValues = Array.from(
        new Set([...value, ...filteredIds])
      );

      onChange(mergedValues);
    }
  };

  return (
    <div className={`relative w-full ${className}`}>
      {label && (
        <label className="fs-14 font-semibold block">
          {label} {required && <span className="color-red">*</span>}
        </label>
      )}

      <div
        ref={triggerRef}
        onClick={handleOpen}
        className="relative fs-14 w-full px-2 py-1.5 mt_8 border br-10 flex items-center justify-between bg-white cursor-pointer"
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {value.length > 0 ? (
            <>
              {value
                .slice(0, singlePreview ? 1 : 2)
                .map((val) => {
                  const option = options.find((o) => o.value === val);
                  if (!option) return null;

                  return (
                    <span
                      key={val}
                      className="bg-[#D7F3DD] rounded-full px-2 py-1 flex items-center gap-1"
                    >
                      {option.label}

                      <span
                        className="cursor-pointer color-red "
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTag(val);
                        }}
                      >
                        <Close />
                      </span>
                    </span>
                  );
                })}

              {value.length > (singlePreview ? 1 : 2) && (
                <span className="bg-[#D7F3DD] rounded-full px-2 py-1 text-xs">
                  +
                  {value.length - (singlePreview ? 1 : 2)}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>

        <ArrowDropDown
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="absolute bg-white border br-10 z-[1000] shadow-xl"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            <li className="p-2 border-b bg-white sticky top-0 z-10">
              <SearchBox
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </li>

            {showSelectAll && filteredOptions.length > 1 && (
              <li className="px-3 py-2 border-b bg-white sticky top-[58px] z-10 bg-white">
                <label
                  className="flex items-center gap-2 cursor-pointer fs-14"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectAll();
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    readOnly
                  />
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </label>
              </li>
            )}

            <div
              style={{
                maxHeight: 280,
                overflowY: "auto",
              }}
              id="scroll_style"
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const checked = value.includes(option.value);

                  return (
                    <li
                      key={option.value}
                      className={`px-3 py-2 cursor-pointer flex items-center gap-2 border-b fs-14 ${checked ? "bg-[#D7F3DD]" : "hover:bg-[#D7F3DD]"
                        }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggleOption(option.value);
                      }}
                    >
                      <input type="checkbox" checked={checked} readOnly />
                      {option.label}
                    </li>
                  );
                })
              ) : (
                <li className="flex p-2 items-center justify-center text-gray-400"
                  style={{
                    maxHeight: 150,
                  }}
                >
                  No results found
                </li>
              )}
            </div>
          </ul>,
          document.body
        )}
    </div>
  );
};

export default MultiSelectDropdown;
